import { ref, computed } from 'vue';
import { opsAuthApi } from '@/api/ops/opsAuth';
import { opsUserApi } from '@/api/ops/opsUser';
import { opsStorageUtil } from '@/utils/opsStorage';

/**
 * 运维平台专用认证 Composable
 * 与用户界面完全隔离
 */
export function useOpsAuth() {
  const currentUser = ref(opsStorageUtil.getUser());
  const isAuthenticated = computed(() => !!currentUser.value);
  
  const loginForm = ref({
    account: '',
    password: ''
  });
  const isLoggingIn = ref(false);
  const loginError = ref('');

  /**
   * 登录
   */
  const login = async (username, password) => {
    loginError.value = '';
    if (!username || !password) {
      loginError.value = '请输入账号和密码';
      throw new Error('请输入账号和密码');
    }
    
    isLoggingIn.value = true;
    try {
      // 使用运维平台专用的认证 API（完全隔离）
      const res = await opsAuthApi.login({
        account: username.trim(),
        password: password
      });
      
      if (!res?.data || !res.data.id) {
        throw new Error('登录失败：未获取到用户信息');
      }
      
      // 验证是否为运维管理员
      if (res.data.role !== 'op') {
        throw new Error('您没有权限访问运维管理平台');
      }
      
      // 保存用户信息到运维平台专用存储
      currentUser.value = res.data;
      opsStorageUtil.saveUser(currentUser.value);
      
      // 获取更详细的用户信息（使用运维平台专用的用户 API）
      try {
        const userInfoRes = await opsUserApi.getUserInfo();
        if (userInfoRes?.data) {
          currentUser.value = { ...currentUser.value, ...userInfoRes.data };
          opsStorageUtil.saveUser(currentUser.value);
        }
      } catch (userInfoError) {
        console.warn('[OpsAuth] 获取用户详细信息失败:', userInfoError);
      }
      
      loginForm.value.password = '';
      
      return {
        user: currentUser.value,
        success: true
      };
    } catch (error) {
      console.error('登录失败:', error);
      
      // 根据错误类型提供更友好的错误提示
      let errorMessage = '登录失败，请稍后重试';
      
      if (error.response) {
        // 服务器返回了错误响应
        const status = error.response.status;
        const data = error.response.data;
        
        if (status === 400 || status === 401) {
          // 认证错误
          errorMessage = data?.message || data?.error || '账号或密码错误，请检查后重试';
        } else if (status === 403) {
          // 权限错误
          errorMessage = data?.message || '您没有权限访问运维管理平台';
        } else if (status === 404) {
          // 接口不存在
          errorMessage = '登录服务暂时不可用，请稍后重试';
        } else if (status >= 500) {
          // 服务器错误
          errorMessage = '服务器错误，请稍后重试或联系管理员';
        } else {
          errorMessage = data?.message || data?.error || `登录失败（错误代码：${status}）`;
        }
      } else if (error.request) {
        // 请求已发出但没有收到响应
        if (error.message && error.message.includes('timeout')) {
          errorMessage = '登录请求超时，请检查网络连接后重试';
        } else if (error.message && error.message.includes('Network')) {
          errorMessage = '网络连接失败，请检查网络设置';
        } else {
          errorMessage = '无法连接到服务器，请检查网络连接';
        }
      } else {
        // 请求配置出错或其他错误
        const msg = error.message || '';
        if (msg.includes('账号') || msg.includes('密码') || msg.includes('登录失败')) {
          errorMessage = msg;
        } else if (msg.includes('权限') || msg.includes('访问')) {
          errorMessage = msg;
        } else if (msg.includes('禁用')) {
          errorMessage = '该账户已被禁用，请联系管理员';
        } else if (msg) {
          errorMessage = msg;
        }
      }
      
      loginError.value = errorMessage;
      throw error;
    } finally {
      isLoggingIn.value = false;
    }
  };

  /**
   * 登出
   */
  const logout = async () => {
    // 清除运维平台专用存储
    if (currentUser.value && currentUser.value.id) {
      opsStorageUtil.clearOpsData(currentUser.value.id);
    }
    
    // 清除用户信息
    opsStorageUtil.clearUser();
    currentUser.value = null;
    
    // 重置登录表单
    loginForm.value.account = '';
    loginForm.value.password = '';
  };

  return {
    currentUser,
    isAuthenticated,
    loginForm,
    isLoggingIn,
    loginError,
    login,
    logout
  };
}

